/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { Component } from 'react';
import { createPath } from 'history';
import type { Location, MatchParams, Query, Route } from '../../common/types.tsx';
import { generateLocationFromPath } from '../../common/utils/generate-location/index.tsx';
import type { RouterActionsType, RouterState } from '../router-store/types.tsx';
import { useRouter } from '../use-router/index.tsx';

export type RedirectProps = {
	to: Location | Route | string;
	push?: boolean;
	params?: MatchParams;
	query?: Query;
};

type RedirectorProps = RedirectProps & {
	actions: RouterActionsType;
	location: RouterState['location'];
};

// eslint-disable-next-line jira/react/no-class-components
class Redirector extends Component<RedirectorProps> {
	static defaultProps = {
		push: false,
	};

	componentDidMount() {
		const { to, location, push, params, query, actions } = this.props;
		const routeAttributes = {
			params,
			query,
			basePath: actions.getBasePath(),
		};
		const newPath =
			// oxlint-disable-next-line no-nested-ternary
			typeof to === 'object'
				? 'path' in to
					? createPath(generateLocationFromPath(to.path, routeAttributes))
					: createPath(to)
				: to;
		const currentPath = createPath(location);
		const action = push ? actions.push : actions.replace;

		if (currentPath === newPath) {
			if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development') {
				// oxlint-disable-next-line no-console
				console.warn(
					`You tried to redirect to the same route you're currently on: "${currentPath}"`,
				);
			}

			return;
		}

		action(newPath);
	}

	render() {
		return null;
	}
}

/**
 * Server-side redirect component that renders a meta refresh tag
 * to instruct the browser to redirect to the specified location.
 */
export const ServerRedirect = ({ to, params, query }: RedirectProps): React.JSX.Element => {
	const getRedirectPath = (): string => {
		if (typeof to === 'string') {
			return to;
		}

		if ('path' in to) {
			const routeAttributes = {
				params,
				query,
			};
			return createPath(generateLocationFromPath(to.path, routeAttributes));
		}

		return createPath(to);
	};

	const redirectPath = getRedirectPath();

	return (
		<meta
			httpEquiv="refresh"
			content={`0; url=${redirectPath}`}
			data-testid="@atlassian/react-resource-router.controllers.redirect.server-redirect-meta"
		/>
	);
};

export const Redirect = (props: RedirectProps): React.JSX.Element => {
	const [{ location }, actions] = useRouter();

	return __SERVER__ ? (
		<ServerRedirect {...props} />
	) : (
		<Redirector actions={actions} location={location} {...props} />
	);
};
